/**
 *
 * CheckoutStep3Page
 *
 */

import messages from 'containers/CheckoutPage/messages';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import translations from 'translations';

import { push } from 'connected-react-router';
import {
  applyVoucher,
  checkoutCart,
  getVouchers,
  setOpenConfirmSameDayDeliveryModal,
  setSomethingChanged,
} from 'containers/CheckoutPage/actions';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import reducer from './reducer';
import saga from './saga';
import { selectLoadingOrderDrafts, selectOrderDrafts } from './selectors';

import { Button, Col, Modal, Row, Spin } from 'antd';
import CartFooter from 'components/Checkout/CartFooter';
import CartTotalBill from 'components/Checkout/CartTotalBill';
import CheckoutOrderTable from 'components/Checkout/CheckoutOrderTable';
import DeliveryInformation from 'components/Checkout/DeliveryInformation';
import backIcon from 'components/Checkout/icons/backIcon.svg';
import notAvailableProductImage from 'components/Checkout/icons/notAvailableProductImage.svg';
import InValidVoucherPopup from 'components/Checkout/Voucher/InValidVoucherPopup';
import VoucherSection from 'components/Checkout/Voucher/VoucherSection';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { VOUCHER_STATUS } from 'containers/CheckoutPage/constants';
import {
  selectCheckout,
  selectLoadingCheckout,
  selectOpenConfirmSameDayDeliveryModal,
  selectOpenModalSomethingChanged,
  selectOrderDraftToken,
  selectVoucherState,
} from 'containers/CheckoutPage/selectors';
import { ContainerState } from 'containers/CheckoutPage/types';
import { selectCart, selectOrderDisabled, selectStore } from 'containers/MainLayout/selectors';
import React, { Fragment, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { OrderDisabledResponse, OrderDraft, SingleSupplierCart, Store } from 'types/schema';
import formatFullAddress from 'utils/formatFullAddress';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';
import { addNoteSubmit, updateDeliveryDateTime, updateDeliveryNotes } from './actions';
import { RootState } from './types';

const Container = styled.div`
  padding-top: 14px;

  ${media.md`
    padding-top: 28px
  `};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 12px;
  padding: 0 16px;

  ${media.lg`
    margin-bottom: 16px;
    padding: 0px;
  `};

  width: fit-content;
  cursor: pointer;
`;

const HeaderTitle = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: #111827;
  font-weight: 600;

  ${media.lg`
    font-size: 30px;
    line-height: 28px;
  `}
`;

const LeftComponentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${media.lg`
    gap: 12px;
  `}
`;

const RightComponentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${media.lg`
    gap: 12px;
    position: relative;
  `}
`;

const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`;

const Title = styled.div`
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 28px;
  color: #242527;
  text-align: center;
`;

const Description = styled.div`
  margin-top: 4px;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: #585a60;
  text-align: center;
`;

const ButtonGoToCart = styled(Button)`
  padding: 8px auto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  width: 200px;
  height: 40px;
`;

const ProductListHeader = styled.div`
  margin: 0px 8px;
  padding: 12px 16px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  background: #fff;
  border-bottom: 1px solid #f2f3f5;

  font-size: 14px;
  line-height: 22px;
  color: #111827;
  font-weight: 600;

  ${media.lg`
    margin: 0;
    padding: 16px 20px;
    font-size: 20px;
    line-height: 28px;
    font-weight: 700;
  `}
`;

const PaymentMethodContainer = styled.div`
  margin: 0px 8px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 24px 0 rgba(58, 85, 9, 0.04);

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  ${media.lg`
    margin: 0;
    padding: 20px 24px;
  `}
`;

const PaymentMethodTitle = styled.div`
  font-size: 16px;
  line-height: 22px;
  color: #111827;
  font-weight: 500;
`;

const PaymentMethodValue = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: #111827;
  font-weight: 400;
`;

interface OwnProps {
  cart: any;
}

interface StateProps {
  cart: any;
  checkout: any;
  loading: boolean;
  checkoutLoading: boolean;
  orderDisabled: OrderDisabledResponse;
  orderDrafts: OrderDraft[];
  openModalSomethingChanged: boolean;
  voucherState: ContainerState['vouchers'];
  draftToken: string;
  store?: Store;
  openConfirmSameDayDeliveryModal: boolean;
}

interface DispatchProps {
  onCheckout: () => void;
  onGoPreviousStep: () => void;
  onGetVouchers: (data: any) => void;
  onApplyVoucher: (data: any) => void;
  onChangeDeliveryDateTime: (data: { draftOrderId: string; supplierId: number; start: string; end: string }) => void;
  onUpdateDeliveryNotes: (data: { draftOrderId: string; supplierId: number; notes: string[] }) => void;
  onAddNote: (data: { draftOrderId: string; supplierId: number; note: string }) => void;
  onGoToStep1FromSomethingChangedModal: () => void;
  setOpenConfirmSameDayDeliveryModal: (data: boolean) => void;
}

type Props = StateProps & DispatchProps & OwnProps;

const CheckoutStep3Page = (props: Props) => {
  const {
    loading,
    checkoutLoading,
    onCheckout,
    onGoPreviousStep,
    checkout,
    cart,
    orderDisabled,
    voucherState,
    onGetVouchers,
    onApplyVoucher,
    store,
    draftToken,
    onChangeDeliveryDateTime,
    onUpdateDeliveryNotes,
    onAddNote,
    openModalSomethingChanged,
    onGoToStep1FromSomethingChangedModal,
    openConfirmSameDayDeliveryModal,
    setOpenConfirmSameDayDeliveryModal,
  } = props;

  const [isOpenConfirmInvalidVoucherPopup, setIsOpenConfirmInvalidVoucherPopup] = useState(false);
  const [isOpenCalendar, setOpenCalendar] = useState(false);
  const rightComponentRef = useRef<HTMLDivElement>(null);
  const leftComponentRef = useRef<HTMLDivElement>(null);
  const voucherRef = useRef<HTMLDivElement>(null);

  const isVoucherNotValid = useMemo(
    () => Boolean(voucherState?.status && voucherState?.status !== VOUCHER_STATUS.APPLIED),
    [voucherState?.status],
  );

  const totalDiscount = useMemo(() => {
    // All discount type logic here
    const deliveryFeeDiscount = cart?.deliveryFee?.isFreeDelivery ? cart?.deliveryFee?.fee : 0;
    const discountVoucher = checkout?.total - checkout?.totalAfterItemDiscount;

    return deliveryFeeDiscount + discountVoucher;
  }, [cart?.deliveryFee, checkout?.total, checkout?.totalAfterItemDiscount]);

  const netTotal = useMemo(() => {
    const serviceFee = cart?.deliveryFee?.isFreeDelivery
      ? 0
      : Number(cart?.deliveryFee?.fee || 0) + Number(cart?.deliveryFee?.additionalVat || 0);

    return checkout?.totalAfterItemDiscount + serviceFee;
  }, [cart?.deliveryFee, checkout?.totalAfterItemDiscount]);

  const grossTotal = useMemo(() => {
    return (netTotal || 0) + totalDiscount;
  }, [netTotal, totalDiscount]);

  const isExistAlcoholProduct = useMemo(() => {
    return cart?.cartSet[0]?.items?.some((it) => it?.product?.category?.parent?.name === 'Alcohol');
  }, [cart?.cartSet]);

  return (
    <Fragment>
      <Helmet>
        <title>{translations(messages.headerStep3)}</title>
        <meta name="description" content={`${translations(messages.headerStep3)}`} />
      </Helmet>
      <Spin spinning={loading}>
        <Container>
          <HeaderContainer onClick={onGoPreviousStep}>
            <SVGIcon src={backIcon} renderAsImg={false} />
            <HeaderTitle>{translations(messages.headerStep3)}</HeaderTitle>
          </HeaderContainer>
          <Row gutter={[24, 8]}>
            <Col lg={16} xs={24}>
              <LeftComponentContainer ref={leftComponentRef}>
                <DeliveryInformation
                  singleSupplierCart={cart?.cartSet[0]}
                  checkout={checkout}
                  draftOrderId={draftToken}
                  deliveryDatetime={cart?.cartSet[0]?.deliveryDatetime}
                  onChangeDeliveryDateTime={onChangeDeliveryDateTime}
                  deliveryAddress={formatFullAddress({
                    ...((store?.newMergedLocation || {}) as any),
                    noteForAddress: store?.addressNote || '',
                  })}
                  onUpdateDeliveryNotes={onUpdateDeliveryNotes}
                  onAddNote={onAddNote}
                  isOpenCalendar={isOpenCalendar}
                  setOpenCalendar={setOpenCalendar}
                />

                {!!cart?.cartSet?.length &&
                  cart?.cartSet?.map((singleSupplierCart: SingleSupplierCart, index: number) => {
                    return (
                      <div key={index}>
                        <ProductListHeader>
                          <FormattedMessage {...utilsMessages.productList} /> ({singleSupplierCart?.items?.length || 0})
                        </ProductListHeader>
                        <CheckoutOrderTable
                          key={`singleSupplierCartStep3-${index}`}
                          name={`step-3-${index}`}
                          singleSupplierCart={singleSupplierCart}
                          productsQuantity={checkout.quantity}
                          itemDiscount={voucherState?.itemDiscount}
                          currentStep={3}
                        />
                      </div>
                    );
                  })}
              </LeftComponentContainer>
            </Col>
            <Col lg={8} xs={24}>
              <RightComponentContainer ref={rightComponentRef}>
                <PaymentMethodContainer>
                  <PaymentMethodTitle>
                    <FormattedMessage {...utilsMessages.paymentMethod} />
                  </PaymentMethodTitle>
                  <PaymentMethodValue>
                    {isExistAlcoholProduct ? (
                      <FormattedMessage {...utilsMessages.BANK_TRANSFER} />
                    ) : (
                      <FormattedMessage {...utilsMessages[cart?.cartSet[0]?.paymentMethod || 'COD']} />
                    )}
                  </PaymentMethodValue>
                </PaymentMethodContainer>
                <div ref={voucherRef}>
                  <VoucherSection
                    voucherState={voucherState}
                    onGetVouchers={onGetVouchers}
                    subTotal={checkout?.total}
                    isOpenConfirmInvalidVoucherPopup={isOpenConfirmInvalidVoucherPopup}
                    setIsOpenConfirmInvalidVoucherPopup={setIsOpenConfirmInvalidVoucherPopup}
                    onApplyVoucher={onApplyVoucher}
                    isVoucherNotValid={isVoucherNotValid}
                  />
                </div>
                <CartTotalBill
                  subTotalBeforeVat={checkout?.totalBeforeVATOfSupplier?.[cart?.cartSet[0]?.supplier?.id!]}
                  subTotal={checkout?.total}
                  cart={cart}
                  voucherState={voucherState}
                  deliveryFee={cart?.deliveryFee}
                  orderDisabled={orderDisabled.orderDisabled || !!voucherState?.loading || !!cart?.loading}
                  loading={checkoutLoading || cart?.loading}
                  netTotalOrder={netTotal}
                  grossTotalOrder={grossTotal}
                  totalDiscount={totalDiscount}
                  currentStep={3}
                  deliveryDate={checkout?.deliveryDate[cart?.cartSet[0]?.supplier?.id!]}
                  setOpenCalendar={setOpenCalendar}
                  openConfirmSameDayDeliveryModal={openConfirmSameDayDeliveryModal}
                  setOpenConfirmSameDayDeliveryModal={setOpenConfirmSameDayDeliveryModal}
                  onGoNext={() => {
                    if (isVoucherNotValid) {
                      setIsOpenConfirmInvalidVoucherPopup(true);
                    } else {
                      onCheckout();
                    }
                  }}
                  parentRef={rightComponentRef}
                  boundaryRef={leftComponentRef}
                  voucherRef={voucherRef}
                />
              </RightComponentContainer>
            </Col>
          </Row>
        </Container>
      </Spin>
      <CartFooter
        deliveryFee={cart.deliveryFee}
        subTotal={checkout?.total}
        cartMoq={cart?.store?.moq || 0}
        currentStep={3}
        netTotalOrder={netTotal}
        totalDiscount={totalDiscount}
        loading={cart?.loading || checkoutLoading}
        orderDisabled={orderDisabled.orderDisabled || !!voucherState?.loading || !!cart?.loading || checkoutLoading}
        onGoNext={onCheckout}
      />
      {openModalSomethingChanged && (
        <Modal open={openModalSomethingChanged} closable={false} footer={false}>
          <ModalWrapper>
            <img src={notAvailableProductImage} alt={'not available products image'} loading="lazy" />
            <div>
              <Title>
                <FormattedMessage {...utilsMessages.somethingChanged} />
              </Title>
              <Description>
                <FormattedMessage {...utilsMessages.somethingChangedDescription} />
              </Description>
            </div>
            <ButtonGoToCart type="primary" onClick={onGoToStep1FromSomethingChangedModal}>
              <FormattedMessage {...utilsMessages.goToCart} />
            </ButtonGoToCart>
          </ModalWrapper>
        </Modal>
      )}
      <InValidVoucherPopup voucherStatus={voucherState?.status} />
    </Fragment>
  );
};

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  orderDrafts: selectOrderDrafts(),
  checkout: selectCheckout(),
  loading: selectLoadingOrderDrafts(),
  checkoutLoading: selectLoadingCheckout(),
  orderDisabled: selectOrderDisabled(),
  openModalSomethingChanged: selectOpenModalSomethingChanged(),
  cart: selectCart(),
  voucherState: selectVoucherState(),
  draftToken: selectOrderDraftToken(),
  store: selectStore(),
  openConfirmSameDayDeliveryModal: selectOpenConfirmSameDayDeliveryModal(),
});

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps): DispatchProps {
  return {
    onGoPreviousStep: () => dispatch(push('/cart')),
    onCheckout: () => dispatch(checkoutCart()),
    onGetVouchers: (data: any) => dispatch(getVouchers(data)),
    onApplyVoucher: (data: string) => dispatch(applyVoucher(data)),
    onChangeDeliveryDateTime: (data) => dispatch(updateDeliveryDateTime(data)),
    onUpdateDeliveryNotes: (data) => dispatch(updateDeliveryNotes(data)),
    onAddNote: (data: any) => dispatch(addNoteSubmit(data)),
    onGoToStep1FromSomethingChangedModal: () => {
      dispatch(setSomethingChanged());
      dispatch(push('/cart'));
    },
    setOpenConfirmSameDayDeliveryModal: (data: boolean) => dispatch(setOpenConfirmSameDayDeliveryModal(data)),
  };
}

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const {
    orderDrafts,
    checkout,
    loading,
    checkoutLoading,
    store,
    openModalSomethingChanged,
    cart,
    voucherState,
    draftToken,
    openConfirmSameDayDeliveryModal,
  } = stateProps;
  return {
    cart: {
      cartSet: orderDrafts || [],
      deliveryFee: cart?.deliveryFee,
      loading: cart?.loading,
      store: cart?.store,
      earliestDeliveryDate: cart?.cartSet[0]?.earliestDate,
    },
    checkout: checkout,
    loading: loading,
    checkoutLoading: checkoutLoading,
    ...dispatchProps,
    ...ownProps,
    orderDisabled: stateProps.orderDisabled,
    store: store,
    openModalSomethingChanged: openModalSomethingChanged,
    voucherState: voucherState,
    draftToken: draftToken,
    openConfirmSameDayDeliveryModal: openConfirmSameDayDeliveryModal,
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps, mergeProps);

// <OwnProps> restricts access to the HOC's other props. This component must not do anything with reducer hoc
const withReducer = injectReducer<OwnProps>({
  key: 'checkoutStep3Page',
  reducer: reducer,
});
// <OwnProps> restricts access to the HOC's other props. This component must not do anything with saga hoc
const withSaga = injectSaga<OwnProps>({ key: 'checkoutStep3Page', saga: saga });

export default compose(withReducer, withSaga, withConnect)(CheckoutStep3Page);
